import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, SignOut } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { formatSectionLabel } from '../utils/sections';

export default function RankingDashboard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSection, setSelectedSection] = useState('all');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await api.getRankingStats();
        if (!resp || !Array.isArray(resp.stats)) {
          throw new Error('Unexpected API response: missing stats array');
        }
        setStats(resp.stats);
      } catch (err) {
        setError('Failed to load ranking statistics: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const sections = useMemo(() => {
    const set = new Set();
    stats.forEach(s => { if (s.section) set.add(s.section); });
    return ['all', ...Array.from(set).sort()];
  }, [stats]);

  const filteredStats = useMemo(() => {
    if (selectedSection === 'all') return stats;
    return stats.filter(s => s.section === selectedSection);
  }, [stats, selectedSection]);

  // Group by model_used
  const grouped = useMemo(() => {
    const map = {};
    filteredStats.forEach(s => {
      const key = s.model_used || 'Unknown';
      if (!map[key]) map[key] = [];
      map[key].push(s);
    });
    // Sort each group by avg_rank
    Object.values(map).forEach(arr => arr.sort((a, b) => a.avg_rank - b.avg_rank));
    return map;
  }, [filteredStats]);

  return (
    <div className="dashboard-page">
      {/* Header */}
      <header className="review-header">
        <div className="review-header-content">
          <div className="review-header-left">
            <button onClick={() => navigate(-1)} className="review-back-link" aria-label="Back to Review">
              <ArrowLeft className="review-icon" />
            </button>
            <h1 className="review-title">Ranking Dashboard</h1>
          </div>
          <div className="review-header-right">
            <button onClick={logout} className="review-logout-btn" aria-label="Logout">
              <SignOut className="review-icon-sm" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="dashboard-content">
        {/* Filter */}
        <div className="dashboard-filter-bar">
          <label htmlFor="section-filter" className="dashboard-filter-label">Section:</label>
          <select
            id="section-filter"
            value={selectedSection}
            onChange={(e) => setSelectedSection(e.target.value)}
            className="review-select-small"
          >
            {sections.map(s => (
              <option key={s} value={s}>
                {s === 'all' ? 'All Sections' : formatSectionLabel(s)}
              </option>
            ))}
          </select>
        </div>

        {loading && <div className="dashboard-loading">Loading statistics...</div>}
        {error && <div className="compare-msg compare-msg-error">{error}</div>}

        {!loading && stats.length === 0 && (
          <div className="dashboard-empty-state">
            <p className="review-empty-title">No ranking data yet</p>
            <p className="review-empty-description">
              Start ranking analyses in the Review page to see statistics here.
            </p>
            <Link to="/review" className="btn btn-primary">Go to Review</Link>
          </div>
        )}

        {!loading && stats.length > 0 && (
          <div className="dashboard-models">
            {Object.entries(grouped).map(([model, modelStats]) => (
              <div key={model} className="dashboard-model-card">
                <h2 className="dashboard-model-name">{model}</h2>
                <table className="dashboard-table">
                  <thead>
                    <tr>
                      <th>Prompt</th>
                      <th>Section</th>
                      <th>Avg Rank</th>
                      <th>Rankings</th>
                    </tr>
                  </thead>
                  <tbody>
                    {modelStats.map((s, i) => (
                      <tr key={`${s.model_used}-${s.prompt_name}-${s.section}`}>
                        <td>{s.prompt_name || 'Default'}</td>
                        <td className="dashboard-cell-section">
                          {formatSectionLabel(s.section)}
                        </td>
                        <td className="dashboard-cell-rank">{typeof s.avg_rank === 'number' ? s.avg_rank.toFixed(2) : 'N/A'}</td>
                        <td>{s.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
